import { prepareFluidImageResize } from './editorFluidImageResize.js';
import {
  clampAutoLayoutSize,
  defaultAutoLayoutFrame,
  defaultAutoLayoutItem,
  normalizeAutoLayoutFrame,
  normalizeAutoLayoutItem,
  type WebEditorAutoLayoutFrame,
  type WebEditorAutoLayoutItem,
} from '../../auto-layout.js';
import type { WebEditorElementFrame, WebEditorLayoutProject } from '../../layout.js';
import type { WebEditorElementDefinition } from '../../element-definition.js';
import { editorNodeIdClass } from './dom.js';
import type { EditorFrame } from './editorPageConfig.js';
import { queryEditorPreviewNode } from './editorPreviewViewport.js';

export interface AutoLayoutOperationsOptions<P extends WebEditorLayoutProject = WebEditorLayoutProject> {
  project: P;
  flowOverrides?: Readonly<Record<string, WebEditorAutoLayoutFrame['flow']>>;
  definitions: readonly WebEditorElementDefinition[];
  activeFrame: EditorFrame;
  cloneProject: (project: P) => P;
  updateProject: (project: P) => void;
}

const keysFor = (viewport: EditorFrame) => viewport === 'desktop'
  ? ({ frame: 'autoLayoutDesktop', item: 'autoLayoutItemDesktop', grid: 'gridDesktop', container: 'gridContainerDesktop', columns: 'gridColumnsDesktop' } as const)
  : ({ frame: 'autoLayoutMobile', item: 'autoLayoutItemMobile', grid: 'gridMobile', container: 'gridContainerMobile', columns: 'gridColumnsMobile' } as const);

const frameFor = (project: WebEditorLayoutProject, id: string): WebEditorElementFrame =>
  project.elementFrames[id] ?? { x: 0, y: 0 };
const nodeFor = (id: string) => queryEditorPreviewNode(`.vp-editor-preview-page .${editorNodeIdClass(id)}`);
const numberStyle = (style: CSSStyleDeclaration, property: string) => Number.parseFloat(style.getPropertyValue(property)) || 0;

const inferFlow = (node: HTMLElement, children: HTMLElement[]): WebEditorAutoLayoutFrame['flow'] => {
  const style = getComputedStyle(node);
  if (style.display === 'grid') return 'grid';
  if (style.display === 'flex') {
    if (style.flexWrap !== 'nowrap') return 'wrap';
    return style.flexDirection.startsWith('column') ? 'vertical' : 'horizontal';
  }
  if (children.length < 2) return 'vertical';
  const rects = children.map(child => child.getBoundingClientRect());
  const firstTop = rects[0]?.top ?? 0;
  const sameRow = rects.every(rect => Math.abs(rect.top - firstTop) < 8);
  if (sameRow) return 'horizontal';
  const hasSharedRow = rects.some((rect, index) => rects.some((other, otherIndex) => otherIndex !== index && Math.abs(rect.top - other.top) < 8));
  return hasSharedRow ? 'wrap' : 'vertical';
};

export const createEditorAutoLayoutOperations = <P extends WebEditorLayoutProject>(options: AutoLayoutOperationsOptions<P>) => {
  const keys = keysFor(options.activeFrame);
  const updateContainer = (elementId: string, update: Partial<WebEditorAutoLayoutFrame>) => {
    const next = options.cloneProject(options.project);
    const current = frameFor(next, elementId);
    const node = nodeFor(elementId);
    const normalized = normalizeAutoLayoutFrame({
      ...(current[keys.frame] ?? defaultAutoLayoutFrame()),
      ...update,
      ...(update.widthMode === 'fixed' && update.fixedWidth === undefined ? { fixedWidth: node?.offsetWidth ?? 320 } : {}),
      ...(update.heightMode === 'fixed' && update.fixedHeight === undefined ? { fixedHeight: node?.offsetHeight ?? 240 } : {}),
    });
    if (!normalized) return;
    next.elementFrames[elementId] = { ...current, ...(update.flow ? { fluidLayout: update.flow === 'wrap' } : {}), [keys.frame]: normalized };
    options.updateProject(next);
  };
  const updateItem = (elementId: string, update: Partial<WebEditorAutoLayoutItem>) => {
    if (frameFor(options.project, elementId).fluidSize && (update.fixedWidth !== undefined || update.fixedHeight !== undefined)) {
      const imageId = options.definitions.find(item => item.id === elementId && item.kind === 'image' || item.maskHostId === elementId)?.id;
      const fluid = imageId && prepareFluidImageResize(options, imageId);
      if (fluid) { fluid.update(update.fixedWidth ?? fluid.startWidth, update.fixedHeight ?? fluid.startHeight); fluid.commit(); return; }
    }
    const next = options.cloneProject(options.project);
    const current = frameFor(next, elementId);
    const node = nodeFor(elementId);
    const normalized = normalizeAutoLayoutItem({
      ...(current[keys.item] ?? defaultAutoLayoutItem()),
      ...update,
      ...(update.widthMode === 'fixed' && update.fixedWidth === undefined ? { fixedWidth: node?.offsetWidth ?? 320 } : {}),
      ...(update.heightMode === 'fixed' && update.fixedHeight === undefined ? { fixedHeight: node?.offsetHeight ?? 240 } : {}),
    });
    if (!normalized) return;
    next.elementFrames[elementId] = { ...current, [keys.item]: normalized };
    if (update.widthMode || update.heightMode) delete next.elementFrames[elementId]!.fluidSize;
    options.updateProject(next);
  };
  const enableContainer = (elementId: string) => {
    if (elementId === 'page.root') return;
    if (frameFor(options.project, elementId)[keys.frame]) return;
    const parentNode = nodeFor(elementId);
    if (!parentNode) return;
    const registeredChildren = options.definitions
      .filter(definition => definition.parentId === elementId)
      .map(definition => ({ definition, node: nodeFor(definition.id) }))
      .filter((entry): entry is { definition: WebEditorElementDefinition; node: HTMLElement } => Boolean(entry.node && entry.node.parentElement === parentNode));
    const flow = options.flowOverrides?.[elementId] ?? inferFlow(parentNode, registeredChildren.map(entry => entry.node));
    const style = getComputedStyle(parentNode);
    const next = options.cloneProject(options.project);
    const parent = { ...frameFor(next, elementId) };
    const isGlobus = elementId === 'globusPartner.layout';
    parent[keys.frame] = {
      ...defaultAutoLayoutFrame(flow),
      gapX: isGlobus ? 64 : numberStyle(style, 'column-gap'),
      gapY: isGlobus ? 34 : numberStyle(style, 'row-gap'),
      paddingX: Math.round((numberStyle(style, 'padding-left') + numberStyle(style, 'padding-right')) / 2),
      paddingY: Math.round((numberStyle(style, 'padding-top') + numberStyle(style, 'padding-bottom')) / 2),
      ...(flow === 'grid' ? { columns: Math.min(20, Math.max(1, registeredChildren.length || 1)) } : {}),
    };
    delete parent[keys.grid]; delete parent[keys.container]; delete parent[keys.columns];
    next.elementFrames[elementId] = parent;
    registeredChildren.forEach((entry, order) => {
      const child = { ...frameFor(next, entry.definition.id) };
      const rect = entry.node.getBoundingClientRect();
      const fill = isGlobus && entry.definition.id === 'globusPartner.copy';
      const fixedMedia = isGlobus && entry.definition.id === 'globusPartner.logoPanel';
      child[keys.item] = {
        ...defaultAutoLayoutItem(order),
        widthMode: fill ? 'fill' : fixedMedia ? 'fixed' : 'hug',
        ...(fixedMedia ? { fixedWidth: clampAutoLayoutSize(rect.width), heightMode: 'fixed', fixedHeight: clampAutoLayoutSize(rect.height) } : {}),
        ...(flow === 'grid' ? { columnSpan: 1, rowSpan: 1 } : {}),
      };
      delete child[keys.grid]; delete child[keys.columns];
      next.elementFrames[entry.definition.id] = child;
    });
    options.updateProject(next);
  };
  const resetContainer = (elementId: string) => {
    const next = options.cloneProject(options.project);
    const parent = { ...frameFor(next, elementId) };
    delete parent[keys.frame];
    parent.fluidLayout = false;
    next.elementFrames[elementId] = parent;
    options.definitions.filter(definition => definition.parentId === elementId).forEach(definition => {
      const child = { ...frameFor(next, definition.id) };
      delete child[keys.item];
      delete child.fluidSize;
      next.elementFrames[definition.id] = child;
    });
    options.updateProject(next);
  };
  const setFixedItemSize = (elementId: string, width?: number, height?: number) => updateItem(elementId, {
    ...(width !== undefined ? { widthMode: 'fixed', fixedWidth: clampAutoLayoutSize(width) } : {}),
    ...(height !== undefined ? { heightMode: 'fixed', fixedHeight: clampAutoLayoutSize(height) } : {}),
  });
  return { enableContainer, resetContainer, setFixedItemSize, updateContainer, updateItem };
};

export type EditorAutoLayoutOperations = ReturnType<typeof createEditorAutoLayoutOperations>;
